import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getPerspective } from "@/data/perspectives";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { SectionShell, CTAPrimary } from "@/components/primitives";
import { useSEO } from "@/hooks/useSEO";

export default function PerspectiveDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const a = getPerspective(slug);
    if (a) { setArticle(a); setNotFound(false); }
    else { setArticle(null); setNotFound(true); }
  }, [slug]);

  useSEO(article ? `${article.title} — HM Beacon` : null, article?.excerpt);

  if (notFound) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="article-not-found">
        <p className="font-heading font-light text-navy text-4xl">Perspective not found.</p>
        <Link to="/perspectives" className="mt-8 inline-block link-underline text-navy">Back to Perspectives</Link>
      </div>
    );
  }

  if (!article) {
    return <div className="pt-40 pb-40 text-center text-ink/40 font-body">Loading…</div>;
  }

  const paragraphs = (article.body || "").split("\n").filter((p) => p.trim());

  return (
    <div data-testid="article-detail-page">
      <article>
        <header className="pt-40 md:pt-52 pb-14 border-b hairline">
          <SectionShell>
            <Reveal>
              <Link to="/perspectives" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-colors mb-10" data-testid="article-back-link">
                <ArrowLeft size={16} /> Perspectives
              </Link>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="flex items-center gap-4 mb-8">
                <span className="overline text-gold">{article.category}</span>
                <span className="w-8 h-px bg-navy/20" />
                <span className="text-xs uppercase tracking-widest text-navy/50">{article.read_time}</span>
              </div>
            </Reveal>
            <h1 className="font-heading font-light text-navy tracking-tight text-[2.4rem] leading-[1.05] md:text-6xl lg:text-7xl max-w-[18ch]">
              <MaskedLines lines={[article.title]} delay={0.1} />
            </h1>
            {article.excerpt && (
              <Reveal delay={0.3}>
                <p className="mt-8 max-w-2xl text-xl leading-relaxed text-ink/70 font-body font-light">{article.excerpt}</p>
              </Reveal>
            )}
          </SectionShell>
        </header>

        <SectionShell className="py-16 md:py-24">
          <div className="max-w-[46rem]">
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={0}>
                <p className="mb-7 text-lg md:text-xl leading-[1.8] text-ink/85 font-body">{p}</p>
              </Reveal>
            ))}
            <div className="mt-16 pt-10 border-t hairline">
              <p className="font-heading font-light text-navy text-2xl md:text-3xl leading-snug max-w-xl">
                Visibility is not the destination. Authority is.
              </p>
              <div className="mt-8">
                <CTAPrimary to="/contact" testId="article-cta">Map Your Visibility</CTAPrimary>
              </div>
            </div>
          </div>
        </SectionShell>
      </article>
    </div>
  );
}
